import type { Workspace } from '@flobi/asset-library/share';

/**
 * "Switched to Acme Studio" — said once, after the editor has reloaded into the workspace a
 * file link lives in (SHARING-MODEL §3.3). The reload would take an in-memory notice with it,
 * so the name rides in session storage until the next load shows it. Moodboard's, in Artwork.
 */
const SWITCHED = 'artwork:switched-workspace';

export function rememberSwitchedWorkspace(workspace: Workspace | null | undefined): void {
  try {
    if (workspace?.name) window.sessionStorage.setItem(SWITCHED, JSON.stringify({ id: workspace.id, name: workspace.name }));
  } catch {
    /* private mode: the switch itself still happened */
  }
}

export function takeSwitchedWorkspace(): Workspace | null {
  try {
    const raw = window.sessionStorage.getItem(SWITCHED);
    if (!raw) return null;
    window.sessionStorage.removeItem(SWITCHED);
    const ws = JSON.parse(raw) as Partial<Workspace>;
    return typeof ws?.id === 'string' && typeof ws.name === 'string' ? { id: ws.id, name: ws.name } : null;
  } catch {
    return null;
  }
}

/**
 * The file a switch was made for, for a minute. Back in the new workspace a refused file is
 * the access screen, never a second switch: if the switch did not hold, the editor would
 * otherwise reload for ever.
 */
const AFTER = 'artwork:switched-for';
const AFTER_MS = 60_000;

export function rememberSwitchedFor(documentId: string): void {
  try {
    window.sessionStorage.setItem(AFTER, JSON.stringify({ id: documentId.toLowerCase(), at: Date.now() }));
  } catch {
    /* the switch still happens; only the guard is lost */
  }
}

/** Whether this load follows a switch made for this file (and forgets it either way). */
export function takeSwitchedFor(documentId: string): boolean {
  try {
    const raw = window.sessionStorage.getItem(AFTER);
    if (!raw) return false;
    window.sessionStorage.removeItem(AFTER);
    const was = JSON.parse(raw) as { id?: unknown; at?: unknown };
    return was?.id === documentId.toLowerCase() && typeof was.at === 'number' && Date.now() - was.at < AFTER_MS;
  } catch {
    return false;
  }
}
